// src/components/JainHeroSection.jsx

import React, { useEffect } from "react";
import { Link } from "react-router-dom";

/* ─── Injected CSS ────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&family=Inter:wght@400;500;600&display=swap');

.jbhero {
  position: relative;
  width: 100%;
  min-height: clamp(420px, 70vh, 640px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
  background: linear-gradient(160deg, #1A0505 0%, #3D0A0A 50%, #5A1010 100%);
}

/* ── Mandala watermark ── */
.jbhero-mandala {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 0;
}
.jbhero-mandala svg {
  width: min(90vw, 580px);
  height: min(90vw, 580px);
  opacity: 0.07;
  animation: jbSpin 60s linear infinite;
}
@keyframes jbSpin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .jbhero-mandala svg { animation: none; } }

/* ── Gold dust particles ── */
.jbhero-dust {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    radial-gradient(circle, rgba(201,150,42,0.18) 1px, transparent 1px),
    radial-gradient(circle, rgba(232,196,106,0.10) 1px, transparent 1px);
  background-size: 38px 38px, 19px 19px;
  background-position: 0 0, 9.5px 9.5px;
}

/* ── Content ── */
.jbhero-content {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: clamp(48px, 8vw, 80px) clamp(16px, 5vw, 48px);
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
}

/* ── Eyebrow ── */
.jbhero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: clamp(10px, 1.2vw, 12px);
  font-weight: 600;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: #C9962A;
  margin-bottom: clamp(16px, 3vw, 24px);
}
.jbhero-eyebrow-line {
  width: 32px;
  height: 1px;
  background: linear-gradient(90deg, transparent, #C9962A);
}
.jbhero-eyebrow-line.right {
  background: linear-gradient(90deg, #C9962A, transparent);
}

/* ── Om symbol above title ── */
.jbhero-om {
  display: block;
  font-size: clamp(36px, 6vw, 56px);
  color: #C9962A;
  line-height: 1;
  margin-bottom: clamp(10px, 2vw, 16px);
  filter: drop-shadow(0 0 12px rgba(201,150,42,0.35));
  font-family: serif;
}

/* ── Title ── */
.jbhero-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(28px, 5.5vw, 58px);
  font-weight: 700;
  color: #FDF6EC;
  line-height: 1.18;
  margin: 0 0 clamp(12px, 2vw, 18px);
  letter-spacing: -0.5px;
}
.jbhero-title em {
  font-style: italic;
  color: #E8C46A;
}

/* ── Subtitle ── */
.jbhero-sub {
  font-size: clamp(14px, 1.8vw, 18px);
  color: rgba(253,246,236,0.78);
  line-height: 1.65;
  max-width: 560px;
  margin: 0 auto clamp(24px, 4vw, 36px);
  font-weight: 400;
}

/* ── Lotus divider ── */
.jbhero-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(10px, 2vw, 18px);
  margin-bottom: clamp(24px, 4vw, 36px);
}
.jbhero-divider-line {
  width: clamp(48px, 8vw, 100px);
  height: 1px;
  background: linear-gradient(90deg, transparent, #C9962A);
}
.jbhero-divider-line.right {
  background: linear-gradient(90deg, #C9962A, transparent);
}

/* ── CTA buttons ── */
.jbhero-ctas {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(10px, 2vw, 16px);
  flex-wrap: wrap;
  margin-bottom: clamp(20px, 3vw, 28px);
}

.jbhero-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: clamp(10px, 1.5vw, 13px) clamp(22px, 3vw, 34px);
  background: linear-gradient(90deg, #C9962A, #A87820);
  border: none;
  border-radius: 32px;
  color: #2C1A0E;
  font-size: clamp(13px, 1.5vw, 15px);
  font-weight: 700;
  font-family: 'Playfair Display', serif;
  letter-spacing: 0.3px;
  cursor: pointer;
  text-decoration: none;
  box-shadow: 0 4px 20px rgba(201,150,42,0.35);
  transition: opacity 0.18s, transform 0.15s;
}
.jbhero-btn-primary:hover { opacity: 0.88; transform: translateY(-1px); }

.jbhero-btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: clamp(9px, 1.4vw, 12px) clamp(20px, 2.5vw, 30px);
  background: transparent;
  border: 1.5px solid rgba(201,150,42,0.65);
  border-radius: 32px;
  color: #E8C46A;
  font-size: clamp(13px, 1.5vw, 15px);
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.18s, border-color 0.18s;
}
.jbhero-btn-secondary:hover { background: rgba(201,150,42,0.1); border-color: #C9962A; }

/* ── Trust pills ── */
.jbhero-trust {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: clamp(6px, 1.5vw, 14px);
}
.jbhero-trust-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border: 1px solid rgba(201,150,42,0.28);
  border-radius: 20px;
  font-size: clamp(10.5px, 1.1vw, 12px);
  color: rgba(253,246,236,0.72);
  font-weight: 500;
  background: rgba(201,150,42,0.06);
  white-space: nowrap;
}
.jbhero-trust-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #C9962A;
  flex-shrink: 0;
}

/* ── Bottom gold bar ── */
.jbhero-bar {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent 0%, #C9962A 30%, #E8C46A 50%, #C9962A 70%, transparent 100%);
}

/* ── Responsive ── */
@media (max-width: 480px) {
  .jbhero-ctas { gap: 10px; }
  .jbhero-btn-primary,
  .jbhero-btn-secondary { width: 100%; justify-content: center; }
}
`;

/* ─── Mandala SVG (pure CSS, no external image) ─────────────────────────── */
const MandalaWatermark = () => (
  <svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" fill="none">
    {/* Rings */}
    {[30, 60, 90, 120, 150, 170, 190].map((r, i) => (
      <circle
        key={i}
        cx="200"
        cy="200"
        r={r}
        stroke="#C9962A"
        strokeWidth={i === 6 ? 1.5 : 0.8}
      />
    ))}
    {/* 16-petal lotus */}
    {Array.from({ length: 16 }, (_, i) => {
      const a = ((i * 360) / 16) * (Math.PI / 180);
      const x1 = 200 + 90 * Math.cos(a),
        y1 = 200 + 90 * Math.sin(a);
      const x2 = 200 + 130 * Math.cos(a + 0.2),
        y2 = 200 + 130 * Math.sin(a + 0.2);
      const x3 = 200 + 130 * Math.cos(a - 0.2),
        y3 = 200 + 130 * Math.sin(a - 0.2);
      return (
        <path
          key={i}
          d={`M200,200 Q${x2},${y2} ${x1},${y1} Q${x3},${y3} 200,200`}
          stroke="#C9962A"
          strokeWidth="0.7"
        />
      );
    })}
    {/* Dharma wheel spokes */}
    {Array.from({ length: 12 }, (_, i) => {
      const a = i * 30 * (Math.PI / 180);
      return (
        <line
          key={i}
          x1={200 + 20 * Math.cos(a)}
          y1={200 + 20 * Math.sin(a)}
          x2={200 + 60 * Math.cos(a)}
          y2={200 + 60 * Math.sin(a)}
          stroke="#C9962A"
          strokeWidth="0.8"
        />
      );
    })}
    <circle cx="200" cy="200" r="20" stroke="#C9962A" strokeWidth="1" />
    <circle
      cx="200"
      cy="200"
      r="60"
      stroke="#C9962A"
      strokeWidth="0.6"
      strokeDasharray="4 4"
    />
    {/* Corner petals */}
    {Array.from({ length: 8 }, (_, i) => {
      const a = (i * 45 + 22.5) * (Math.PI / 180);
      const x = 200 + 160 * Math.cos(a),
        y = 200 + 160 * Math.sin(a);
      return (
        <circle
          key={i}
          cx={x}
          cy={y}
          r="8"
          stroke="#C9962A"
          strokeWidth="0.6"
        />
      );
    })}
  </svg>
);

/* ─── Lotus divider icon ────────────────────────────────────────────────── */
const LotusIcon = ({ size = 28 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <ellipse cx="14" cy="16" rx="4" ry="9" fill="#C9962A" opacity="0.9" />
    <ellipse
      cx="14"
      cy="16"
      rx="4"
      ry="8"
      fill="#C9962A"
      opacity="0.85"
      transform="rotate(-30 14 16)"
    />
    <ellipse
      cx="14"
      cy="16"
      rx="4"
      ry="8"
      fill="#C9962A"
      opacity="0.85"
      transform="rotate(30 14 16)"
    />
    <ellipse
      cx="14"
      cy="16"
      rx="3.5"
      ry="6"
      fill="#C9962A"
      opacity="0.6"
      transform="rotate(-58 14 16)"
    />
    <ellipse
      cx="14"
      cy="16"
      rx="3.5"
      ry="6"
      fill="#C9962A"
      opacity="0.6"
      transform="rotate(58 14 16)"
    />
    <ellipse cx="14" cy="24" rx="7" ry="2.5" fill="#C9962A" opacity="0.25" />
  </svg>
);

/* ─── Component ─────────────────────────────────────────────────────────── */
const JainHeroSection = () => {
  useEffect(() => {
    const id = "jbhero-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = CSS;
      document.head.appendChild(el);
    }
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  return (
    <section className="jbhero" aria-label="Jain Var Vadhu hero">
      {/* Rotating mandala watermark */}
      <div className="jbhero-mandala" aria-hidden="true">
        <MandalaWatermark />
      </div>

      {/* Gold dust grid */}
      <div className="jbhero-dust" aria-hidden="true" />

      {/* Content */}
      <div className="jbhero-content">
        {/* Eyebrow */}
        <div className="jbhero-eyebrow" aria-label="Jain Matrimony">
          <span className="jbhero-eyebrow-line" aria-hidden="true" />
          Jain Matrimony · Trusted by Values
          <span className="jbhero-eyebrow-line right" aria-hidden="true" />
        </div>

        {/* Om */}
        <span className="jbhero-om" aria-label="Om">
          ꣽ
        </span>

        {/* Title */}
        <h1 className="jbhero-title">
          Find Your <em>Sacred</em> Match
        </h1>

        {/* Subtitle */}
        <p className="jbhero-sub">
          Where Ahimsa guides the heart and Dharma unites souls. Jain Var Vadhu —
          a matrimonial platform rooted in divine values, built for families who
          honour the Jain way of life.
        </p>

        {/* Lotus divider */}
        <div className="jbhero-divider" aria-hidden="true">
          <div className="jbhero-divider-line" />
          <LotusIcon size={32} />
          <div className="jbhero-divider-line right" />
        </div>

        {/* CTA buttons */}
        <div className="jbhero-ctas">
          <a href="/#register-form" className="jbhero-btn-primary">
            🪷 Join Jain Var Vadhu
          </a>
          <Link to="/login" className="jbhero-btn-secondary">
            Sign In ☸
          </Link>
        </div>

        {/* Trust pills */}
        <div
          className="jbhero-trust"
          role="list"
          aria-label="Platform highlights"
        >
          {[
            "100% Verified Jain Profiles",
            "Family-Approved Matchmaking",
            "Guided by Jain Values",
            "Privacy First",
          ].map((t) => (
            <span key={t} className="jbhero-trust-pill" role="listitem">
              <span className="jbhero-trust-dot" aria-hidden="true" />
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Gold bottom bar */}
      <div className="jbhero-bar" aria-hidden="true" />
    </section>
  );
};

export default JainHeroSection;
